 import React, { useState, useEffect } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import { useAccount } from '~/hooks/useAccount'
import { useBalance } from '~/hooks/useBalance'
import { api } from '~/services/api'
import { TOKENS } from '~/constants'
import { FaucetButton } from './FaucetButton'
import { Button } from './Button'

export const WalletButton = () => {
  const { connected, disconnect, publicKey } = useWallet()
  const { isAuthenticated, user, login, logout, isLoading, error } = useAccount()
  const { getBalance, mutate: refetchBalances } = useBalance()
  const [showModal, setShowModal] = useState(false)
  const [isGitHubConnected, setIsGitHubConnected] = useState(false)
  const [checkingGitHub, setCheckingGitHub] = useState(false)

  const solBalance = getBalance('SOL')
  const dagBalance = getBalance(TOKENS.PLATFORM.address)

  // Check GitHub connection status when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      checkGitHubConnection()
    }
  }, [isAuthenticated, user])

  const checkGitHubConnection = async () => {
    if (!isAuthenticated) return
    
    setCheckingGitHub(true)
    try {
      const profile = await api.profile.get()
      setIsGitHubConnected(profile.isGithubConnected)
    } catch (err) {
      console.error('Failed to check GitHub connection:', err)
      setIsGitHubConnected(false)
    } finally {
      setCheckingGitHub(false)
    }
  }

  const handleClick = async () => {
    if (!connected) {
      // Let WalletMultiButton handle wallet connection
      return
    }
    
    if (connected && !isAuthenticated) {
      // Sign in with wallet signature
      try {
        await login()
      } catch (err) {
        console.error('Login failed:', err)
      }
    } else if (isAuthenticated) {
      // Show modal for authenticated users
      setShowModal(true)
    }
  }

  const handleSignOut = async () => {
    await logout()
    setShowModal(false)
  }

  const handleDisconnect = async () => {
    // Clear authentication from localStorage
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_data')
    
    // Disconnect wallet
    await disconnect()
    setShowModal(false)
  }

  const copyAddressToClipboard = async () => {
    if (publicKey) {
      try {
        await navigator.clipboard.writeText(publicKey.toBase58())
        // You could add a toast notification here
      } catch (err) {
        console.error('Failed to copy address:', err)
      }
    }
  }

  const handleLinkGitHub = async () => {
    try {
      // Use the dedicated callback route
      const callbackUrl = `${window.location.origin}/github-callback`
      console.log('callbackUrl', callbackUrl)
      const { url } = await api.profile.github.getAuthUrl(callbackUrl)
      
      // Open GitHub OAuth in a new tab
      window.open(url, '_blank')
      
    } catch (err) {
      console.error('Failed to start GitHub connection:', err)
      // You could show a toast notification here
    }
  }

  const getButtonText = () => {
    if (!connected) {
      return 'Select Wallet'
    }
    
    if (connected && !isAuthenticated) {
      if (isLoading) {
        return 'Signing In...'
      }
      // Show wallet address when connected but not authenticated
      const address = publicKey?.toBase58() || ''
      return `Sign in (${address.slice(0, 4)}...${address.slice(-4)})`
    }
    
    if (isAuthenticated && user) {
      const displayName = user.githubId || `${user.solanaWalletAddress.slice(0, 4)}...${user.solanaWalletAddress.slice(-4)}`
      return displayName
    }
    
    return 'Select Wallet'
  }

  const getButtonStyle = () => {
    const baseStyle = {
      height: '48px',
      fontSize: '14px',
    }

    // Red styling when connected but not authenticated (needs sign in)
    if (connected && !isAuthenticated) {
      return {
        ...baseStyle,
        backgroundColor: '#dc2626',
        color: 'white',
      }
    }

    // Authenticated styling
    if (connected && isAuthenticated) {
      return {
        ...baseStyle,
        backgroundColor: '#1f2937',
        border: '1px solid #374151',
      }
    }

    // Default styling for not connected
    return baseStyle
  }

  // If not connected, use the standard WalletMultiButton
  if (!connected) {
    return (
      <WalletMultiButton style={getButtonStyle()} />
    )
  }

  // If connected, create a custom button that handles authentication
  return (
    <>
      <div className="relative group">
        {/* Custom button for authentication states */}
        <button
          onClick={handleClick}
          disabled={isLoading}
          className="wallet-adapter-button wallet-adapter-button-trigger"
          style={getButtonStyle()}
        >
          <i className="wallet-adapter-button-start-icon"></i>
          {getButtonText()}
          {connected && isAuthenticated && (
            <i className="wallet-adapter-button-end-icon">
              <svg width="11" height="6" viewBox="0 0 11 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="m1 1 4.5 4L10 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </i>
          )}
        </button>
        
        {/* Hidden WalletMultiButton for wallet management when authenticated */}
        {connected && isAuthenticated && (
          <div className="absolute top-0 left-0 opacity-0 pointer-events-none">
            <WalletMultiButton style={{ 
              height: '48px',
              fontSize: '14px'
            }} />
          </div>
        )}
        
        {error && (
          <div className="absolute top-full left-0 mt-1 text-xs text-red-400 whitespace-nowrap">
            {error}
          </div>
        )}
      </div>

      {/* Modal for authenticated users - positioned under button */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowModal(false)}
          />
          
          {/* Modal Content - positioned under navbar */}
          <div className="absolute top-full right-0 z-50 mt-4 bg-[#1a1d1f] border border-[#212425] rounded-lg p-0 w-80 shadow-xl">
            {/* GitHub Section */}
            <div className="p-4 border-b border-[#212425]">
              <h3 className="text-sm font-semibold text-white mb-3">GitHub</h3>
              {checkingGitHub ? (
                <div className="text-sm text-white/60">Checking...</div>
              ) : isGitHubConnected && user?.githubId ? (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#60e25e] flex items-center justify-center">
                    <span className="text-black font-semibold text-xs">
                      {user.githubId[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{user.githubId}</div>
                    <a 
                      href={`https://github.com/${user.githubId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#60e25e] text-xs hover:underline"
                    >
                      View Profile
                    </a>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleLinkGitHub}
                  type="primary"
                  size="sm"
                  className="w-full"
                >
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 0.5C5.5 0.5 0.5 5.5 0.5 12C0.5 17.1 3.8 21.4 8.3 22.9C8.9 23 9.1 22.6 9.1 22.3V20.3C6.1 21 5.4 18.9 5.4 18.9C4.9 17.6 4.1 17.2 4.1 17.2C3 16.5 4.2 16.5 4.2 16.5C5.4 16.6 6 17.7 6 17.7C7.1 19.5 8.9 19 9.2 18.8C9.3 18 9.6 17.5 10 17.3C7.5 17 4.9 16 4.9 11.6C4.9 10.4 5.3 9.4 6 8.7C5.9 8.4 5.4 7.2 6.1 5.6C6.1 5.6 7.1 5.3 9.1 6.6C10 6.4 11 6.3 12 6.3C13 6.3 14 6.4 14.9 6.6C16.9 5.3 17.9 5.6 17.9 5.6C18.6 7.2 18.1 8.4 18 8.7C18.7 9.4 19.1 10.4 19.1 11.6C19.1 16 16.5 17 14 17.3C14.5 17.7 14.9 18.4 14.9 19.5V22.3C14.9 22.6 15.1 23 15.7 22.9C20.2 21.4 23.5 17.1 23.5 12C23.5 5.5 18.5 0.5 12 0.5Z" fill="currentColor"/>
                    </svg>
                    Link GitHub Account
                  </div>
                </Button>
              )}
            </div>
            {/* Balance Section */}
            <div className="p-4 border-b border-[#212425]">
              <h3 className="text-sm font-semibold text-white mb-3">Balance</h3>
              <div className="space-y-2 flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <span className="text-white/60 text-sm">SOL</span>
                  <span className="text-white text-sm">{solBalance.toFixed(4)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/60 text-sm">DAG</span>
                  <span className="text-white text-sm">{dagBalance.toFixed(2)}</span>
                </div>
                <div className="flex border-t border-[#212425] w-full">
                  <FaucetButton />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 space-y-2">
              <button
                onClick={copyAddressToClipboard}
                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors text-sm text-white"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4H9C7.89543 4 7 4.89543 7 6V14C7 15.1046 7.89543 16 9 16H16C17.1046 16 18 15.1046 18 14V6C18 4.89543 17.1046 4 16 4Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M8 2C6.89543 2 6 2.89543 6 4V2H15C16.1046 2 17 2.89543 17 4V18C17 19.1046 16.1046 20 15 20H6C4.89543 20 4 19.1046 4 18V4C4 2.89543 4.89543 2 6 2H8Z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Copy Address
              </button>
              
              <button
                onClick={handleDisconnect}
                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-red-600/20 transition-colors text-sm text-red-400"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5m0 0l-5-5m5 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Disconnect Wallet
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
} 